import React, { useCallback, useMemo } from 'react';
import {
  ResponsiveGridLayout,
  useContainerWidth,
  type LayoutItem,
  type Layout,
  type ResponsiveLayouts,
  verticalCompactor,
} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { WidgetRenderer } from './WidgetRenderer';
import { DashboardConfig } from '../../services/dashboards.service';

// Grid configuration.
//
// Breakpoints are measured against the GRID CONTAINER width, not the window.
// On desktop the fixed 256px sidebar (lg:pl-64) plus page padding makes the
// container ~720px at a 1024px window and ~1616px at 1920px. So the designed
// 12-column layout must engage well below 1200px or it never shows across most
// of the desktop range. We keep 12 columns for any container ≥ ~640px (every
// desktop width) and only collapse to fewer columns on genuinely small/tablet
// containers, so the dashboard renders consistently from 1024px up.
const GRID_COLS = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 };
const GRID_BREAKPOINTS = { lg: 1024, md: 640, sm: 500, xs: 380, xxs: 0 };
const ROW_HEIGHT = 80;
const MARGIN: readonly [number, number] = [12, 12];

export interface DashboardGridProps {
  dashboard: DashboardConfig;
  dashboardData?: any;
  agentData?: any;
  slaData?: any;
  trendsData?: any[];
  recentActivityData?: any[];
  loading?: boolean;
  dateRange?: { start: string; end: string };
  isEditMode: boolean;
  /**
   * Live Dashboard only: KPI cards open the tickets behind their figure,
   * scoped to this team filter. Left unset in Dashboard Management.
   */
  drillDown?: { teamId?: string };
  onLayoutChange?: (layout: LayoutItem[]) => void;
  onRemoveWidget?: (widgetId: string) => void;
  onToggleWidgetVisibility?: (widgetId: string, visible: boolean) => void;
  onConfigureWidget?: (widgetId: string) => void;
  /** Map of widgetType -> available chart types (from catalog) */
  chartTypesMap?: Record<string, string[]>;
  /** Callback when chart type is changed inline on a widget */
  onChartTypeChange?: (widgetId: string, chartType: string) => void;
}

export function DashboardGrid({
  dashboard,
  dashboardData,
  agentData,
  slaData,
  trendsData,
  recentActivityData,
  loading,
  dateRange,
  isEditMode,
  drillDown,
  onLayoutChange,
  onRemoveWidget,
  onToggleWidgetVisibility,
  onConfigureWidget,
  chartTypesMap,
  onChartTypeChange,
}: DashboardGridProps) {
  const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 1200 });

  // Build layout from widget positions, falling back to dashboard.layout
  const layouts = useMemo((): ResponsiveLayouts => {
    const widgets = dashboard.widgets || [];
    const layoutMap = new Map(
      (dashboard.layout || []).map((l) => [l.widgetId, l]),
    );

    const lgLayout: LayoutItem[] = widgets
      .filter((w) => isEditMode || w.isVisible)
      .map((w, i) => {
        const pos = w.position as { x: number; y: number; w: number; h: number } | undefined;
        const saved = layoutMap.get(w.id);
        return {
          i: w.id,
          x: pos?.x ?? saved?.x ?? (i % 4) * 3,
          y: pos?.y ?? saved?.y ?? Math.floor(i / 4) * 3,
          w: pos?.w ?? saved?.w ?? 3,
          h: pos?.h ?? saved?.h ?? 3,
          minW: 2,
          minH: 1,
          static: !isEditMode,
        };
      });

    // `md` shares the 12-column layout so the designed positions render
    // identically across the whole desktop range (≥1024px window). Smaller
    // breakpoints (sm/xs/xxs) are auto-generated by react-grid-layout from this.
    return { lg: lgLayout, md: lgLayout };
  }, [dashboard.widgets, dashboard.layout, isEditMode]);

  const handleLayoutChange = useCallback(
    (currentLayout: Layout) => {
      if (isEditMode && onLayoutChange) {
        onLayoutChange(currentLayout as LayoutItem[]);
      }
    },
    [isEditMode, onLayoutChange],
  );

  const visibleWidgets = useMemo(
    () => (dashboard.widgets || []).filter((w) => isEditMode || w.isVisible),
    [dashboard.widgets, isEditMode],
  );

  if (visibleWidgets.length === 0) {
    if (!isEditMode) return null;
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
        <p className="text-lg font-medium">No widgets yet</p>
        <p className="text-sm mt-1">Click "Add Widget" to get started</p>
      </div>
    );
  }

  return (
    <div ref={containerRef as React.RefObject<HTMLDivElement>} className="dashboard-grid">
      {mounted && (
        <ResponsiveGridLayout
          className="layout"
          width={width}
          layouts={layouts}
          breakpoints={GRID_BREAKPOINTS}
          cols={GRID_COLS}
          rowHeight={ROW_HEIGHT}
          margin={MARGIN}
          // No outer padding: the grid shares its edges with the stat row and
          // the attention row below it instead of sitting 12px inside them.
          containerPadding={[0, 0]}
          compactor={verticalCompactor}
          dragConfig={{ enabled: isEditMode, handle: '.widget-drag-handle' }}
          resizeConfig={{ enabled: isEditMode }}
          onLayoutChange={handleLayoutChange}
        >
          {visibleWidgets.map((widget) => (
            <div
              key={widget.id}
              data-widget-type={widget.widgetType}
              className={`rounded-lg ${isEditMode ? 'ring-1 ring-dashed ring-gray-300 dark:ring-gray-600' : 'overflow-hidden'} ${
                !widget.isVisible && isEditMode ? 'opacity-50' : ''
              }`}
            >
              <div className={isEditMode ? 'widget-drag-handle cursor-grab active:cursor-grabbing h-full overflow-hidden' : 'h-full'}>
                <WidgetRenderer
                  widget={widget}
                  dashboardData={dashboardData}
                  agentData={agentData}
                  slaData={slaData}
                  trendsData={trendsData}
                  recentActivityData={recentActivityData}
                  loading={loading}
                  dateRange={dateRange}
                  isEditMode={isEditMode}
                  onRemove={onRemoveWidget}
                  onToggleVisibility={onToggleWidgetVisibility}
                  onConfigure={onConfigureWidget}
                  chartTypes={chartTypesMap?.[widget.widgetType]}
                  onChartTypeChange={onChartTypeChange}
                  drillDown={drillDown}
                />
              </div>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </div>
  );
}